import React, { useState } from 'react';
import { useAuth, useLanguage, api } from '../../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Label } from '../ui/label';
import { MessageSquare, Volume2, VolumeX } from 'lucide-react';
import { toast } from 'sonner';

const SettingsAIIntervention = () => {
  const { user, updateUser } = useAuth();
  const { language, t } = useLanguage();
  const [saving, setSaving] = useState(false);

  const currentMode = user?.ai_intervention_mode || 'proactive';

  const handleChange = async (mode) => {
    if (mode === currentMode) return;
    setSaving(true);
    try {
      const res = await api.put('/users/profile', { ai_intervention_mode: mode });
      updateUser(res.data);
      toast.success(
        mode === 'silent'
          ? t('settings_subs.tracker_mode_active')
          : (language === 'fr' ? 'Mode Proactif active. L\'Assistant te guide activement.' : 'Proactive mode active. Assistant guides you actively.')
      );
    } catch {
      toast.error(language === 'fr' ? 'Erreur de sauvegarde' : 'Save error');
    } finally {
      setSaving(false);
    }
  };

  const options = [
    {
      value: 'proactive',
      icon: MessageSquare,
      titleFr: 'Proactif',
      titleEn: 'Proactive',
      descFr: 'L\'Assistant commente les repas, donne des conseils, felicite les bons choix et alerte les ecarts.',
      descEn: 'Assistant comments on meals, gives advice, praises good choices and flags issues.',
      detailsFr: ['Feedback nutritionnel detaille', 'Conseils et suggestions', 'Points et gamification', 'Alertes hors-piste'],
      detailsEn: ['Detailed nutritional feedback', 'Advice and suggestions', 'Points and gamification', 'Off-track alerts'],
    },
    {
      value: 'silent',
      icon: VolumeX,
      titleFr: 'Silencieux / Mode Tracker',
      titleEn: 'Silent / Tracker Mode',
      descFr: 'L\'Assistant enregistre les macros et calcule les points, mais ne genere aucun commentaire ni conseil.',
      descEn: 'Assistant records macros and calculates points, but generates zero comments or advice.',
      detailsFr: ['Macros calcules automatiquement', 'Points gagnes normalement', 'Zero commentaire genere', 'Confirmation simple: "Repas enregistre"'],
      detailsEn: ['Macros calculated automatically', 'Points earned normally', 'Zero generated comments', 'Simple confirmation: "Meal logged"'],
    },
  ];

  return (
    <Card
      className={`bg-card border-border transition-all duration-300 ${currentMode === 'silent' ? 'border-blue-500/50 shadow-lg shadow-blue-500/10' : ''}`}
      data-testid="settings-ai-intervention"
    >
      <CardHeader>
        <CardTitle className="font-heading text-xl uppercase tracking-tight flex items-center gap-2">
          {currentMode === 'silent'
            ? <VolumeX className="w-5 h-5 text-blue-400" />
            : <Volume2 className="w-5 h-5 text-emerald-400" />
          }
          {language === 'fr' ? 'Intervention de l\'Assistant' : 'Assistant Intervention'}
        </CardTitle>
        <CardDescription>
          {language === 'fr'
            ? 'Choisis comment l\'Assistant YUMIX interagit avec tes repas.'
            : 'Choose how YUMIX Assistant interacts with your meals.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {options.map((opt) => {
          const Icon = opt.icon;
          const isSelected = currentMode === opt.value;
          const details = language === 'fr' ? opt.detailsFr : opt.detailsEn;

          return (
            <button
              key={opt.value}
              onClick={() => handleChange(opt.value)}
              disabled={saving}
              data-testid={`ai-intervention-${opt.value}`}
              className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 ${
                isSelected
                  ? opt.value === 'silent'
                    ? 'border-blue-500 bg-blue-500/10'
                    : 'border-emerald-500 bg-emerald-500/10'
                  : 'border-border bg-secondary/30 hover:bg-secondary/50'
              } ${saving ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
            >
              <div className="flex items-center gap-3 mb-2">
                <Icon className={`w-5 h-5 ${
                  isSelected
                    ? opt.value === 'silent' ? 'text-blue-400' : 'text-emerald-400'
                    : 'text-muted-foreground'
                }`} />
                <span className={`font-heading text-sm uppercase tracking-tight ${
                  isSelected ? 'text-foreground' : 'text-muted-foreground'
                }`}>
                  {language === 'fr' ? opt.titleFr : opt.titleEn}
                </span>
                {isSelected && (
                  <span className={`ml-auto text-[10px] uppercase tracking-widest font-bold ${
                    opt.value === 'silent' ? 'text-blue-400' : 'text-emerald-400'
                  }`}>
                    {language === 'fr' ? 'Actif' : 'Active'}
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground mb-2">
                {language === 'fr' ? opt.descFr : opt.descEn}
              </p>
              <ul className="space-y-1">
                {details.map((d, i) => (
                  <li key={i} className="flex items-center gap-2 text-xs text-muted-foreground/80">
                    <div className={`w-1.5 h-1.5 rounded-full ${
                      isSelected
                        ? opt.value === 'silent' ? 'bg-blue-400' : 'bg-emerald-400'
                        : 'bg-muted-foreground/30'
                    }`} />
                    {d}
                  </li>
                ))}
              </ul>
            </button>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default SettingsAIIntervention;
